import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, Loader2, CheckCircle2, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';
import { ReglesPaiement, REGLES_DOMIUM } from '../invitation/ReglesPaiement';
import { EtatAttestation } from '../invitation/EtatAttestation';
import { PrelevementAutoOption } from '../invitation/PrelevementAutoOption';
import { RemplacerCarteBtn, carteUrl, useConfirmCarte } from '../invitation/RemplacerCarteBtn';

const fmt = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

const Echeancier = ({ echeances }) => (
  <ol className="space-y-1" data-testid="paiement-echeancier">
    {echeances.map((e) => (
      <li key={e.index} className="flex items-center justify-between text-sm" data-testid={`paiement-echeance-${e.index}`}>
        <span className="flex items-center gap-1.5 text-slate-700"><CalendarClock className="h-3.5 w-3.5 text-slate-400" /> {e.index === 0 ? 'Acompte 60 %' : `Échéance ${e.index + 1}/4`} — {fmtD(e.date_echeance)}</span>
        <span className={e.statut === 'paye' ? 'text-emerald-700 font-medium' : 'text-slate-700'}>{fmt(e.montant)} {e.statut === 'paye' ? '✓ payé' : ''}</span>
      </li>
    ))}
  </ol>
);

export const PaiementBox = ({ api, token, data, params, onPaid }) => {
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [accepted, setAccepted] = useState(false);
  const [auto, setAuto] = useState(false);
  useConfirmCarte(onPaid);
  const p = data.paiement || {};
  const ttc = Number(data.contribution_ttc || 0);
  const ea = data.etat_attestation || {};

  useEffect(() => {
    const sid = params.get('session_id');
    if (params.get('paiement') === 'annule') setMsg({ ok: '', err: 'Paiement annulé : vous pouvez réessayer.' });
    if (params.get('paiement') !== 'ok' || !sid) return;
    setBusy('check');
    axios.get(`${api}/${token}/paiement/status/${sid}`)
      .then((r) => {
        if (r.data.payment_status === 'paid') { setMsg({ ok: `Paiement de ${fmt(r.data.montant)} reçu. Merci !`, err: '' }); onPaid(); }
        else setMsg({ ok: '', err: 'Paiement en cours de validation, actualisez la page dans quelques instants.' });
      })
      .catch(() => setMsg({ ok: '', err: 'Vérification du paiement impossible pour le moment.' }))
      .finally(() => setBusy(null));
  }, [api, token, params, onPaid]);

  const locked = !data.regles_acceptees_at && !accepted && !(data.paiement?.echeances || []).length;
  const pay = async (mode) => {
    if (locked) { setMsg({ ok: '', err: 'Veuillez lire et accepter les règles de paiement' }); return; }
    setBusy(mode);
    try {
      const r = await axios.post(`${api}/${token}/paiement`, { mode, origin: window.location.origin, regles_acceptees: true, prelevement_auto: mode === '4x' && auto });
      window.location.href = r.data.checkout_url;
    } catch (e) { setMsg({ ok: '', err: e.response?.data?.detail || 'Paiement indisponible' }); setBusy(null); }
  };

  if (p.statut === 'paye') {
    return <><EtatAttestation s={ea} /><p className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded p-3 text-sm" data-testid="paiement-paye"><CheckCircle2 className="h-4 w-4" /> {msg.ok || `Contribution réglée (${fmt(ttc)}).`}</p></>;
  }
  const next = (p.echeances || []).find((e) => e.statut !== 'paye');
  const acompte = Math.round(ttc * 60) / 100;
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-3" data-testid="paiement-box">
      <h2 className="flex items-center gap-2 font-semibold text-emerald-900"><CreditCard className="h-4 w-4" /> Régler ma contribution par carte</h2>
      {busy === 'check' && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
      {msg.ok && <p className="text-sm text-emerald-700" data-testid="paiement-success">{msg.ok}</p>}
      <EtatAttestation s={ea} />
      <ReglesPaiement accepted={accepted} acceptedAt={data.regles_acceptees_at} onChange={setAccepted} regles={REGLES_DOMIUM} />
      {p.statut === 'partiel' ? (
        <>
          <p className="text-sm text-slate-700">Paiement en 4 fois en cours : {fmt(p.paye_ttc)} réglés, reste {fmt(p.reste_ttc)}.</p>
          <Echeancier echeances={p.echeances} />
          {data.prelevement_auto?.actif && <PrelevementAutoOption actif carte={data.prelevement_auto.carte} />}
          <RemplacerCarteBtn url={carteUrl.domium(token)} aCarte={!!data.prelevement_auto?.carte} testId="paiement-carte-btn" />
          {ea.etat_paiement === 'resilie' && ea.regularisation ? (
            <Button onClick={() => pay('4x')} disabled={!!busy} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="paiement-regul-btn">
              {busy === '4x' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />} Régulariser et réactiver mon contrat ({fmt(ea.regularisation.total)})
            </Button>
          ) : next && (
            <Button onClick={() => pay('4x')} disabled={!!busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="paiement-echeance-btn">
              {busy === '4x' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />} Payer l'échéance {next.index + 1}/4 ({fmt(next.montant)})
            </Button>
          )}
        </>
      ) : (
        <>
          <Button onClick={() => pay('total')} disabled={!!busy || locked} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="paiement-total-btn">
            {busy === 'total' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />} Payer la totalité par carte ({fmt(ttc)})
          </Button>
          {data.eligible_4x && <PrelevementAutoOption checked={auto} onChange={setAuto} />}
          {data.eligible_4x && (
            <Button variant="outline" onClick={() => pay('4x')} disabled={!!busy || locked} className="w-full border-emerald-300 text-emerald-800 h-auto py-2 whitespace-normal" data-testid="paiement-4x-btn">
              {busy === '4x' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Payer en 4 fois par carte : acompte 60 % ({fmt(acompte)}) puis 3 × {fmt((ttc - acompte) / 3)}
            </Button>
          )}
        </>
      )}
      {msg.err && <p className="text-sm text-red-600" data-testid="paiement-error">{msg.err}</p>}
      <p className="text-[11px] text-slate-500">Paiement sécurisé par Stripe. {data.eligible_4x ? 'En 4 fois, les échéances suivantes se règlent depuis cette même page, à 30, 60 et 90 jours.' : ''}</p>
    </div>
  );
};
